import { useFocusEffect } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef } from 'react';

import { DealFinePrint } from '@/components/DealFinePrint';
import { ErrorState } from '@/components/ErrorState';
import { FaqCard } from '@/components/FaqCard';
import { useSocialVideoActions } from '@/hooks/useSocialVideoActions';
import { useVideoPicker } from '@/hooks/useVideoPicker';
import type { useVideoScroll } from '@/hooks/useVideoScroll';
import { playbookReadable, playbookStep } from '@/lib/socialProgress';
import { voltrStore } from '@/stores/voltr.store';
import { voltrUploadStore } from '@/stores/voltrUpload.store';
import type { Action, Deal, DealCta, DealStep } from '@/types/deal';

import { SocialCreateCard } from './SocialCreateCard';
import { SocialExampleVideos } from './SocialExampleVideos';
import { SocialInlineUpload } from './SocialInlineUpload';
import { SocialEarningsCard } from './SocialEarningsCard';
import { SocialProductCard } from './SocialProductCard';
import { SocialRewardTiles } from './SocialRewardTiles';
import { SocialSection } from './SocialSection';
import { SocialStepper } from './SocialStepper';

export type SocialDealBodyProps = {
  deal: Deal;
  action?: Action;
  onCtaPress: (step: DealStep, cta?: DealCta) => void;
  /** Opens the brand's brief; advances `step` when it's the active one. */
  onReadBrief: (step?: DealStep) => void;
  ctaLoading: boolean;
  ctaError: string | null;
  /** The page's scroll-to-video (the hero's "Fix 1 video"): cards register, a folded step opens. */
  videoScroll?: Pick<ReturnType<typeof useVideoScroll>, 'registerVideo' | 'focusRequest'>;
};

/**
 * The MarketPryce Social deal page below the hero (Figma "Apply" frame, v2):
 * What you get → the brand's products → Let’s create → Example videos
 * → Your Deal Progress, then FAQ and fine print when the deal has them. Every section
 * is server copy and hides when empty. Products come from /social, which
 * only answers with a brand once the user is a member.
 */
export const SocialDealBody = observer(function SocialDealBody({
  deal,
  action,
  onCtaPress,
  onReadBrief,
  ctaLoading,
  ctaError,
  videoScroll,
}: SocialDealBodyProps) {
  const { remove, removing, videoError } = useSocialVideoActions(action);
  const error = ctaError ?? videoError;
  // voltrStore holds one deal's /social: load on arrival, and again on coming
  // back here after another social deal (pushed over this one) took its place.
  const loadedFor = useRef<string | null>(null);
  useFocusEffect(
    useCallback(() => {
      if (loadedFor.current === deal.slug && voltrStore.socialSlug === deal.slug) return;
      loadedFor.current = deal.slug;
      void voltrStore.load(deal.slug);
    }, [deal.slug]),
  );
  // The due date counts from delivery: when the product lands, re-read /social for it.
  const deliveredAt = action?.voltr?.sample?.delivered_at ?? null;
  const seenDelivery = useRef(deliveredAt);
  useEffect(() => {
    if (seenDelivery.current === deliveredAt) return;
    seenDelivery.current = deliveredAt;
    if (voltrStore.socialSlug === deal.slug) void voltrStore.load(deal.slug);
  }, [deliveredAt, deal.slug]);

  const copy = deal.marketing_copy;
  const social = copy?.social;
  const loaded = voltrStore.socialSlug === deal.slug ? voltrStore.social : null;
  const brand = loaded?.brand;
  // Once ordered, only the product they picked. Before that, a one-product
  // brand shows its product; the "Choose 1 free product" row of several is
  // off for now (call 2026-10-26). To bring it back, restore this line:
  // const products = loaded?.ordered_product ? [loaded.ordered_product] : (brand?.products ?? []);
  const catalog = brand?.products ?? [];
  const products = loaded?.ordered_product ? [loaded.ordered_product] : catalog.length === 1 ? catalog : [];
  // The row opens the playbook once the server has opened that step (as soon
  // as the product is ordered), and marks the step done when it's still open.
  const playbook = playbookStep(deal);
  const briefReadable = playbookReadable(deal, action);
  const tiles = social?.reward_tiles ?? [];
  // This athlete's (with their due date) once /social is in; the deal's own until then.
  const specs = loaded?.create_specs ?? social?.create_specs;
  const videos = social?.example_videos ?? [];
  const removed = action?.voltr?.removed;
  // Uploading needs the brand's video rights on screen (from /social), not just their version.
  const rights = loaded?.rights;
  const rightsVersion = rights?.text ? rights.version : null;
  const { pick, pickError } = useVideoPicker(action && rightsVersion ? { actionId: action.id, rightsVersion } : null);
  const rightsProblem = !loaded
    ? voltrStore.loadError
    : rightsVersion
      ? null
      : "We couldn't load the brand's video rights, so uploads can't start yet.";
  // The upload step's own entry: another finished step can carry add_label too (a brand's order page).
  const uploadKey = deal.steps_config.steps.find((s) => s.ctas?.some((c) => c.action === 'voltr_upload'))?.key;
  const uploadEntry = uploadKey ? action?.step_states?.[uploadKey] : undefined;

  // Bare on the page, as in the Figma: only What you get, the product and Let’s create are cards.
  const progress = (
    <SocialSection title="Your Deal Progress" testID="social-progress">
      {removed ? <ErrorState bare title={removed.title} body={removed.body} testID="social-removed" /> : null}
      <SocialStepper
        deal={deal}
        action={action}
        onCtaPress={onCtaPress}
        ctaLoading={ctaLoading}
        videoHandlers={{
          thumbnails: loaded?.video_thumbnails,
          ads: loaded?.video_ads,
          // A sent-back video's replacement comes straight from the camera roll, onto the step.
          // Hidden while picks are pending below: a new pick would silently replace them.
          onReplace: action && voltrUploadStore.hasPending(action.id) ? undefined : (video) => void pick(video.id),
          onRemove: remove,
          removing,
          registerVideo: videoScroll?.registerVideo,
          focusRequest: videoScroll?.focusRequest,
        }}
        uploader={
          action ? (
            <SocialInlineUpload
              actionId={action.id}
              rightsText={rightsVersion ? (rights?.text ?? null) : null}
              pick={(replaces) => void pick(replaces)}
              pickError={pickError}
              rightsProblem={rightsProblem}
              onRetry={() => void voltrStore.load(deal.slug)}
              addLabel={uploadEntry?.add_label}
              videos={uploadEntry?.videos}
              register={videoScroll?.registerVideo}
            />
          ) : undefined
        }
        earnings={loaded?.earnings ? <SocialEarningsCard earnings={loaded.earnings} testID="social-earnings" /> : undefined}
        testID="social-stepper"
      />
      {error ? <ErrorState bare title="Couldn't continue" body={error} /> : null}
    </SocialSection>
  );

  return (
    <>
      {tiles.length > 0 ? (
        <SocialSection title="What you get" testID="social-what-you-get">
          <SocialRewardTiles tiles={tiles} />
          {products.length > 0 ? <SocialProductCard products={products} testID="social-products" /> : null}
        </SocialSection>
      ) : products.length > 0 ? (
        <SocialProductCard products={products} testID="social-products" />
      ) : null}

      {specs && (specs.rows?.length || specs.tips?.length) ? (
        <SocialSection title="Let’s create" testID="social-create">
          <SocialCreateCard
            specs={specs}
            onReadBrief={briefReadable ? () => onReadBrief(playbook) : undefined}
            testID="social-create-card"
          />
        </SocialSection>
      ) : null}

      {videos.length > 0 ? (
        <SocialSection title="Example videos" testID="social-examples">
          <SocialExampleVideos videos={videos} />
        </SocialSection>
      ) : null}

      {progress}

      <FaqCard title="FAQ" items={copy?.faq} testID="deal-faq" />
      <DealFinePrint terms={deal.terms} disclosure={copy?.compliance_disclosure} testID="deal-fine-print" />
    </>
  );
});
